import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import {
  getAvailableModels,
  getCurrentModel,
  selectModel,
  type ModelConfig,
  type AvailableModelsResponse,
  type CurrentModelResponse,
} from '../services/settings.service';
import { usePermissions } from './PermissionsContext';

// Types
export interface SettingsContextType {
  // State
  availableModels: ModelConfig[];
  groupedModels: { [provider: string]: ModelConfig[] };
  currentModel: CurrentModelResponse | null;
  defaultModel: string;
  environment: string;
  isLoading: boolean;
  error: string | null;

  // Actions
  refreshModels: () => Promise<void>;
  refreshCurrentModel: () => Promise<void>;
  setSelectedModel: (modelId: string) => Promise<boolean>;
  clearError: () => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = (): SettingsContextType => {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return ctx;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider = ({ children }: SettingsProviderProps) => {
  // The session cookie is HttpOnly, so we can't sniff it from document.cookie.
  // Use the canonical auth state from PermissionsContext instead.
  const { authenticated } = usePermissions();
  // State
  const [availableModels, setAvailableModels] = useState<ModelConfig[]>([]);
  const [groupedModels, setGroupedModels] = useState<{ [provider: string]: ModelConfig[] }>({});
  const [currentModel, setCurrentModel] = useState<CurrentModelResponse | null>(null);
  const [defaultModel, setDefaultModel] = useState<string>('gpt-5-chat');
  const [environment, setEnvironment] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch available models
  const refreshModels = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response: AvailableModelsResponse = await getAvailableModels();
      setAvailableModels(response.models);
      setGroupedModels(response.grouped);
      setDefaultModel(response.default_model);
      setEnvironment(response.environment);
    } catch (err: any) {
      console.error('Error fetching available models:', err);
      setError(err.message || 'Failed to fetch available models');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch current model selection
  const refreshCurrentModel = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getCurrentModel();
      setCurrentModel(response);
      setEnvironment(response.environment);
    } catch (err: any) {
      // Don't show error if user is not logged in (401)
      if (err.response?.status !== 401) {
        console.error('Error fetching current model:', err);
        setError(err.message || 'Failed to fetch current model');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Set selected model
  const setSelectedModel = useCallback(async (modelId: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await selectModel(modelId);
      setCurrentModel({
        model_id: response.model_id,
        display_name: response.display_name,
        provider: response.provider,
        description: response.description,
        environment: environment,
      });
      return true;
    } catch (err: any) {
      console.error('Error selecting model:', err);
      setError(err.message || 'Failed to select model');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [environment]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load models once the user is authenticated. Re-runs if the user logs
  // in/out during the session so the dropdown is always populated when the
  // settings dialog is opened.
  useEffect(() => {
    if (authenticated) {
      refreshModels();
    }
  }, [authenticated, refreshModels]);

  // Context value
  const value: SettingsContextType = {
    availableModels,
    groupedModels,
    currentModel,
    defaultModel,
    environment,
    isLoading,
    error,
    refreshModels,
    refreshCurrentModel,
    setSelectedModel,
    clearError,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext;
